// UAE Plate Preview — now backed by the unified `UAEPlate` component (12 official designs).
// This file preserves the OLD public API so all existing call-sites keep working:
//   <UAEPlatePreview emirate="rak" plateCode="B" plateNumber="30026" plateType="private" language="ar" />
//   getAvailablePlateCodes(emirate, plateType)
//   validatePlateCodeCompatibility(plateCode, plateType, language)
//
// Behind the scenes we normalize the legacy `emirate="rak"` key to `ras_al_khaimah`
// (the unified component's canonical key). Anywhere a real designer plate exists,
// we render it. When it doesn't exist yet, we show a clean placeholder rather than
// the previous hand-drawn fallback.
import UAEPlate, { hasPlateDesign } from '../plates/UAEPlate';

// Legacy → canonical emirate key mapping
const EMIRATE_MAP = {
  rak: 'ras_al_khaimah',
  ras_al_khaimah: 'ras_al_khaimah',
  ras_alkhaimah: 'ras_al_khaimah',
  uaq: 'umm_al_quwain',
  umm_al_quwain: 'umm_al_quwain',
  abu_dhabi: 'abu_dhabi',
  abudhabi: 'abu_dhabi',
  dubai: 'dubai',
  sharjah: 'sharjah',
  ajman: 'ajman',
  fujairah: 'fujairah',
};

// Emirate display names (still needed for the caption under the plate)
const EMIRATE_LABELS = {
  abu_dhabi: { ar: 'أبوظبي', en: 'ABU DHABI' },
  dubai: { ar: 'دبي', en: 'DUBAI' },
  sharjah: { ar: 'الشارقة', en: 'SHARJAH' },
  ajman: { ar: 'عجمان', en: 'AJMAN' },
  umm_al_quwain: { ar: 'أم القيوين', en: 'UMM AL QUWAIN' },
  ras_al_khaimah: { ar: 'رأس الخيمة', en: 'RAS AL KHAIMAH' },
  fujairah: { ar: 'الفجيرة', en: 'FUJAIRAH' },
};

const CATEGORY_LABELS = {
  private: { ar: 'خصوصي', en: 'PRIVATE' },
  motorcycle: { ar: 'دراجة نارية', en: 'MOTORCYCLE' },
  classic: { ar: 'كلاسيك', en: 'CLASSIC' },
};

export const UAEPlatePreview = ({ emirate, plateCode, plateNumber, plateType = 'private', language = 'ar' }) => {
  const canonicalEmirate = EMIRATE_MAP[emirate] || emirate;
  const category = plateType || 'private';
  const emirateLabel = EMIRATE_LABELS[canonicalEmirate] || { ar: '', en: '' };
  const catLabel = CATEGORY_LABELS[category] || CATEGORY_LABELS.private;

  const supported = hasPlateDesign(canonicalEmirate, category);

  return (
    <div className="flex flex-col items-center gap-3" data-testid="uae-plate-preview">
      {supported ? (
        <UAEPlate
          emirate={canonicalEmirate}
          category={category}
          code={plateCode || ''}
          number={plateNumber || ''}
          width={category === 'motorcycle' ? 260 : 380}
        />
      ) : (
        // Graceful placeholder for combos the designer hasn't shipped yet
        <div
          className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm px-4"
          style={{ width: 380, height: 96 }}
          data-testid="uae-plate-unsupported"
        >
          {language === 'ar'
            ? 'تصميم اللوحة غير متوفر لهذه الإمارة/الفئة بعد'
            : 'Plate design not available for this emirate/category yet'}
        </div>
      )}

      <div className="text-center">
        <p className="text-sm font-bold text-gray-700">
          {language === 'ar' ? emirateLabel.ar : emirateLabel.en}
          {' — '}
          {language === 'ar' ? catLabel.ar : catLabel.en}
        </p>
        {(plateCode || plateNumber) && (
          <p className="text-xs text-gray-500">
            {plateCode || ''} {plateNumber || ''}
          </p>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Plate code rules (unchanged public API)
// ─────────────────────────────────────────────────────────────────────────────

export const getAvailablePlateCodes = (emirate, plateType) => {
  // Classic plates - all emirates have "كلاسيك" as the only code
  if (plateType === 'classic') {
    return [{ value: 'classic', label: 'كلاسيك', labelEn: 'Classic' }];
  }

  const canonical = EMIRATE_MAP[emirate] || emirate;

  // Ras Al Khaimah specific rules
  if (canonical === 'ras_al_khaimah') {
    if (plateType === 'private') {
      return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => ({
        value: letter, label: letter, labelEn: letter,
      }));
    }
    if (plateType === 'motorcycle') {
      return [
        { value: 'cat1', label: 'الفئة الأولى', labelEn: 'Category 1' },
        { value: 'cat4', label: 'الفئة الرابعة', labelEn: 'Category 4' },
      ];
    }
  }

  // Default: A to Z
  return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => ({
    value: letter, label: letter, labelEn: letter,
  }));
};

export const validatePlateCodeCompatibility = (plateCode, plateType, language) => {
  if (plateType === 'classic' && plateCode && plateCode !== 'classic') {
    return {
      valid: false,
      message: language === 'ar'
        ? 'صنف اللوحة غير متطابق مع الكود - لوحات كلاسيك تتطلب كود كلاسيك'
        : 'Plate type does not match code - Classic plates require Classic code',
    };
  }
  if (plateCode === 'classic' && plateType && plateType !== 'classic') {
    return {
      valid: false,
      message: language === 'ar'
        ? 'صنف اللوحة غير متطابق مع الكود - كود كلاسيك يتطلب صنف كلاسيك'
        : 'Plate type does not match code - Classic code requires Classic type',
    };
  }
  return { valid: true, message: '' };
};
